import { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollToRecordingProps {
  activities: { id: string }[];
  activityTileSize: number;
}

export function useScrollToRecording({ activities, activityTileSize }: ScrollToRecordingProps) {
  const [activeRecordingId, setActiveRecordingId] = useState<string>('');

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const recordingRefs = useRef<Map<string, HTMLDivElement>>(new Map()); // map of recording ids to div elements for quick lookup
  const autoScrollRef = useRef(false); // flag to determine if scroll is caused by code or by user
  const autoScrollTimeoutRef = useRef<number | null>(null); // timeout to reset programmatic scroll flag bc smooth scrolling has no reliable "done" event
  const recordingVisibilityRatioRef = useRef<Map<string, number>>(new Map()); // mapping for how visible each recording is

  const observerRef = useRef<IntersectionObserver | null>(null);

  const setRecordingRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        recordingRefs.current.set(id, el);
        observerRef.current?.observe(el);
      } else {
        const node = recordingRefs.current.get(id);
        if (node) observerRef.current?.unobserve(node);
        recordingRefs.current.delete(id);
        recordingVisibilityRatioRef.current.delete(id);
      }
    },
    []
  );

  const handleScroll = useCallback(() => {
    // if scroll is caused by code, do not update active activity
    if (autoScrollRef.current) {
      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
      }
      // reset auto scroll
      autoScrollTimeoutRef.current = window.setTimeout(() => {
        autoScrollRef.current = false;
      }, 250);
    }
  }, []);

  const autoScrollToRecording = useCallback(
    (id: string) => {
      const index = activities.findIndex((activity) => activity.id === id);
      if (index === -1) return;

      autoScrollRef.current = true;
      setActiveRecordingId(id);

      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
      }

      autoScrollTimeoutRef.current = window.setTimeout(() => {
        autoScrollRef.current = false;
      }, 1000);

      if (scrollContainerRef.current) {
        const tilePosY = index * activityTileSize;

        scrollContainerRef.current.scrollTo({
          top: tilePosY,
          behavior: 'smooth',
        });
      }
    },
    [activities, activityTileSize]
  );

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // compute how much of each list entry is visible
        entries.forEach((entry) => {
          const id = entry.target.id;
          recordingVisibilityRatioRef.current.set(id, entry.intersectionRatio);
        });

        if (autoScrollRef.current) return;

        let mostVisibleItem: string | null = null;
        let highestRatio = 0;

        recordingVisibilityRatioRef.current.forEach((ratio, id) => {
          if (ratio > highestRatio) {
            highestRatio = ratio;
            mostVisibleItem = id;
          }
        });

        if (mostVisibleItem) {
          const targetId = mostVisibleItem;
          setActiveRecordingId((prev) => (prev === targetId ? prev : targetId));
        }
      },
      { root, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    // re-observe all currently known refs
    recordingRefs.current.forEach((el) => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return {
    activeRecordingId,
    scrollContainerRef,
    setRecordingRef,
    autoScrollToRecording,
    handleScroll,
  };
}
